// Map state = a pure, read-only projection of the progression engine.
// It calls progress getters only (domainSummary, unitState, canOpen, currentUnit) and
// never writes progress or storage. The map cannot unlock anything.

import { CITIES, missionKind } from './mapData.js';
import { unitXp } from '../core/xp.js';

/** City (domain) state: complete | current | available | pending | locked. */
export function cityState(progress, curriculum, domainId) {
  const s = progress.domainSummary(domainId);
  if (s.complete) return 'complete';
  if (!s.unlocked) return 'locked';
  if (s.pending === s.total) return 'pending';
  const current = curriculum.domains.find((d) => { const x = progress.domainSummary(d.id); return x.unlocked && !x.complete; });
  return current?.id === domainId ? 'current' : 'available';
}

/**
 * Mission (unit) state: complete | current | available | locked | pending,
 * plus `incident` for an open troubleshooting mission or final challenge.
 */
export function missionState(progress, domain, unit) {
  const st = progress.unitState(domain.id, unit.id);
  const kind = missionKind(unit);
  let state;
  if (st === 'complete') state = 'complete';
  else if (st === 'pending') state = 'pending';
  else if (st === 'locked') state = 'locked';
  else state = progress.currentUnit(domain.id)?.id === unit.id ? 'current' : 'available';
  const incident = (kind === 'TROUBLESHOOTING' || kind === 'CHALLENGE') && (state === 'current' || state === 'available');
  return { state, unitState: st, kind, incident, canOpen: progress.canOpen(domain.id, unit.id) };
}

/**
 * City network overlay state (presentation only): facility/link = online | planned |
 * outage, and whether the city's final incident is active. Derived from unit states.
 */
export function cityNetwork(progress, domain, city) {
  const net = city.network;
  if (!net) return null;
  const done = (uid) => !uid || progress.unitState(domain.id, uid) === 'complete';
  const fin = domain.units.find((u) => u.id === net.incidentUnit);
  const incident = !!fin && ['available', 'in-progress'].includes(progress.unitState(domain.id, fin.id));
  const st = (x) => (incident && x.incident ? 'outage' : done(x.by) ? 'online' : 'planned');
  return {
    incident,
    facilities: net.facilities.map((f) => ({ ...f, state: st(f) })),
    links: net.links.map((l) => ({ ...l, state: st(l) })),
  };
}

/** Why a mission cannot be started (or null when it can). */
export function lockReason(progress, curriculum, domain, unit) {
  const st = progress.unitState(domain.id, unit.id);
  if (st !== 'locked' && st !== 'pending') return null;
  if (!progress.domainSummary(domain.id).unlocked) {
    const prev = curriculum.domains[curriculum.domains.indexOf(domain) - 1];
    const city = CITIES.find((c) => c.domainId === prev?.id);
    return `Locked — complete Domain ${prev?.number} (${city ? city.name : prev?.title}) to unlock this city.${st === 'pending' ? ' Its missions are also still being built.' : ''}`;
  }
  if (st === 'pending') return 'IMPLEMENTATION PENDING — this mission has not been built yet.';
  const idx = domain.units.indexOf(unit);
  const blocker = domain.units.slice(0, idx).find((p) => progress.unitState(domain.id, p.id) !== 'complete');
  return blocker ? `Locked — complete Mission ${String(domain.units.indexOf(blocker) + 1).padStart(2, '0')} · ${blocker.title} first.` : 'Locked.';
}

/** Operator status (presentation only — derived from completed units, never stored). */
export function operatorStats(progress, curriculum) {
  let done = 0; let open = 0; let xp = 0;
  for (const d of curriculum.domains) {
    for (const u of d.units) {
      if (!u.implemented) continue;
      open++;
      if (progress.unitState(d.id, u.id) === 'complete') {
        done++;
        // 100 XP a mission, 500 a final challenge, minus hints used (src/core/xp.js)
        xp += unitXp(missionKind(u), { status: 'complete', ...(progress.unitRecord?.(d.id, u.id) || {}) });
      }
    }
  }
  // Incident Labs (Phase 4): XP of server-verified incidents, when the progress object has it (the
  // verified reward view, src/core/verifiedLabs.js). It adds XP only — never missions done/open.
  const incident = Math.max(0, Number(progress.incidentXp?.()) || 0);
  xp += incident;
  return { xp, level: 1 + Math.floor(xp / 500), done, open, toNext: 500 - (xp % 500) };
}

// ---------------------------------------------------------------- map routes
// #/map                      world (Philippines)
// #/map/<domainId>           city
// #/map/<domainId>/site      RouteZero site
// #/map/<domainId>/site/<u>  site + mission briefing
// #/map/<domainId>/info      Philippines view + city info panel
export function parseMapRoute(parts) {
  const [domainId, site, unitId] = parts;
  if (!domainId || !CITIES.some((c) => c.domainId === domainId)) return { level: 'world' };
  if (site === 'info') return { level: 'world', domainId, preview: true };
  if (site !== 'site') return { level: 'city', domainId };
  return { level: 'site', domainId, unitId: unitId || null };
}
export function mapHref(r) {
  if (!r || r.level === 'world') return r?.preview && r.domainId ? `#/map/${r.domainId}/info` : '#/map';
  if (r.level === 'city') return `#/map/${r.domainId}`;
  return `#/map/${r.domainId}/site${r.unitId ? `/${r.unitId}` : ''}`;
}
